/** Atrapa los fallos de pintado para que una pantalla rota no deje el portal en blanco.
 *
 * El caso que más importa es el de **una versión nueva publicada mientras hay una pestaña
 * abierta**: la pestaña vieja pide un fragmento (el editor, por ejemplo) con un nombre que ya no
 * existe y el servidor contesta con otra cosa. Se arregla recargando, así que se recarga **una
 * vez** sola, y si no basta se enseña un mensaje en vez de entrar en un bucle.
 */
import { Component, type ReactNode } from 'react'

const FALLO_DE_FRAGMENTO = /dynamically imported module|Importing a module script failed|Failed to fetch/i
const CLAVE = 'portal.recarga-por-version'

function recargarSiToca(error: unknown): boolean {
  if (!FALLO_DE_FRAGMENTO.test(String((error as Error)?.message ?? error))) return false
  try {
    const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0)
    if (Date.now() - ultima < 15_000) return false          // ya se intentó hace nada
    sessionStorage.setItem(CLAVE, String(Date.now()))
  } catch {
    return false
  }
  window.location.reload()
  return true
}

// Cuando Vite no consigue cargar un fragmento, avisa con este evento antes de fallar.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (e) => {
    if (recargarSiToca(new Error('Failed to fetch dynamically imported module'))) e.preventDefault()
  })
}

interface Props {
  children: ReactNode
  /** Dentro de una pantalla (la barra lateral sigue ahí) en vez de a pantalla completa. */
  compacto?: boolean
}

export default class Limite extends Component<Props, { fallo: boolean }> {
  state = { fallo: false }

  static getDerivedStateFromError() {
    return { fallo: true }
  }

  componentDidCatch(error: unknown) {
    recargarSiToca(error)
  }

  render() {
    if (!this.state.fallo) return this.props.children
    return (
      <div className={this.props.compacto ? 'limite limite--compacto' : 'limite'} role="alert">
        <strong>No se ha podido mostrar esto.</strong>
        <p>
          Lo más probable es que se haya publicado una versión nueva del portal mientras tenías
          la pestaña abierta. Recarga la página y debería volver.
        </p>
        <button className="boton boton--principal" onClick={() => window.location.reload()}>
          Recargar
        </button>
      </div>
    )
  }
}
